/**
 * Tailwind CSS implementation of Button component
 * Uses Seeds button CSS classes from button.css
 */

import React from "react";
import type { TypeButtonProps } from "./ButtonTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

const appearanceClasses: Record<string, string> = {
  primary: "seeds-btn-primary",
  secondary: "seeds-btn-secondary",
  destructive: "seeds-btn-destructive",
  placeholder: "seeds-btn-placeholder",
  pill: "seeds-btn-pill",
  unstyled: "seeds-btn-unstyled",
  "ai-primary": "seeds-btn-ai-primary",
  "ai-secondary": "seeds-btn-ai-secondary",
  "ai-outline": "seeds-btn-ai-outline",
};

const sizeClasses: Record<string, string> = {
  default: "",
  large: "seeds-btn-lg",
  small: "seeds-btn-sm",
};

export const ButtonTailwind = React.forwardRef<
  HTMLButtonElement,
  TypeButtonProps
>(
  (
    {
      href,
      appearance = "unstyled",
      active = false,
      disabled = false,
      external,
      children,
      fullWidth = false,
      size = "default",
      onClick,
      title,
      type,
      qa = {},
      as,
      ariaLabel,
      "aria-label": nativeAriaLabel,
      "aria-disabled": nativeAriaDisabled,
      innerRef: _innerRef,
      className,
      ...rest
    }: TypeButtonProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    // Determine the element type
    const Component = (as || (href ? "a" : "button")) as any;

    // Build the class name
    const classes = cn(
      "seeds-btn",
      appearanceClasses[appearance] || appearanceClasses.unstyled,
      appearance !== "pill" && sizeClasses[size],
      {
        "seeds-btn-disabled": disabled,
        "seeds-btn-active": active,
        "seeds-btn-fullwidth": !!fullWidth,
      },
      className
    );

    // Build props based on whether it's a link or button.
    // Do NOT set the HTML `disabled` attribute — ButtonTypes.ts documents that we use
    // aria-disabled instead so the button stays in the tab order for keyboard/screen-reader users.
    const elementProps = href
      ? {
          href,
          target: external ? "_blank" : undefined,
          rel: external ? "noopener noreferrer" : undefined,
        }
      : {
          // Default to "button" but let a consumer-provided type (e.g. "submit")
          // win — previously this hardcoded value was spread after {...rest}.
          type: type ?? "button",
        };

    // Guard: prevent click handler from firing when disabled (matches pre-PR Button.tsx behavior)
    const handleClick = disabled
      ? (e: React.MouseEvent) => {
          e.preventDefault();
        }
      : onClick;

    return (
      <Component
        // QA defaults precede {...rest} so a consumer-provided data-qa-button
        // (e.g. via {...qaAttributes(...)} or the qa prop) overrides the default.
        data-qa-button={title || ""}
        data-qa-button-isdisabled={disabled === true}
        {...rest}
        className={classes}
        title={title}
        ref={ref}
        onClick={handleClick}
        aria-label={ariaLabel ?? nativeAriaLabel}
        // Merged like aria-label above: an explicit aria-disabled prop is honored,
        // but disabled={true} always forces "true" regardless of what was passed.
        aria-disabled={disabled ? disabled : nativeAriaDisabled}
        {...qa}
        {...elementProps}
      >
        {children}
      </Component>
    );
  }
);

ButtonTailwind.displayName = "ButtonTailwind";
